/**
 * Lightweight self-hosted page-view tracker for the marketing site.
 *
 * Drop into a marketing page like:
 *   import { useMarketingTracker } from "../lib/marketingTracker";
 *   useMarketingTracker();
 *
 * Privacy footprint:
 *   - No third-party network calls.
 *   - Session id is a random string in sessionStorage that dies with the
 *     tab — we never link visits across browser sessions.
 *   - We don't store IPs server-side; the only identity is the
 *     ephemeral session id and the optional UTM params from the URL.
 *
 * Aggregated views are visible to the primary admin at /admin/insights
 * via GET /api/admin/insights/marketing.
 */
import { useEffect } from "react";
import axios from "axios";

const SESSION_KEY = "porchlight_mkt_session";
const RECIPIENT_KEY = "porchlight_mkt_recipient";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function recipientToken() {
  // `pl_r` arrives on the first click from a broadcast email link. Persist it
  // for the session so navigation within the site stays attributed.
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("pl_r");
    if (fromUrl) {
      sessionStorage.setItem(RECIPIENT_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(RECIPIENT_KEY) || "";
  } catch {
    return new URLSearchParams(window.location.search).get("pl_r") || "";
  }
}

function ensureSessionId() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      // 16 random chars — enough entropy to dedupe sessions in a 30-day window.
      sid = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    // Private mode or sandboxed iframe — fall back to a per-call id.
    return "anon_" + Math.random().toString(36).slice(2, 10);
  }
}

export function useMarketingTracker() {
  useEffect(() => {
    if (!BACKEND) return;
    let cancelled = false;
    const fire = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        await axios.post(`${BACKEND}/api/marketing/track`, {
          path: window.location.pathname || "/",
          referrer: document.referrer || "",
          session_id: ensureSessionId(),
          utm_source: params.get("utm_source") || "",
          utm_medium: params.get("utm_medium") || "",
          utm_campaign: params.get("utm_campaign") || "",
          utm_content: params.get("utm_content") || "",
          pl_r: recipientToken(),
        });
      } catch {
        // Best-effort: never surface tracking errors to the visitor.
      }
    };
    if (!cancelled) fire();
    return () => { cancelled = true; };
  }, []);
}
